import axios from 'axios';
import { AuthResponse, ServicesResponse, ProfileResponse, User, Service } from '../types';

// Все запросы идут через API Gateway (относительные URL)
const API_BASE_URL = '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем токен к запросам
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Обработка 401 — пробуем refresh token, если не получилось — logout
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // Нет refresh token — logout
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Уже обновляем — ждём
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        const response = await axios.post('/api/auth/refresh', { refreshToken });
        if (response.data.success && response.data.data?.token) {
          const newToken = response.data.data.token;
          localStorage.setItem('token', newToken);
          api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await api.post<AuthResponse>('/api/auth/login', { email, password });
  return response.data;
};

export const getMe = async (): Promise<User> => {
  const response = await api.get<ProfileResponse>('/api/auth/me');
  return response.data.data!;
};

// Services
export const getServices = async (): Promise<Service[]> => {
  try {
    const response = await api.get<ServicesResponse>('/api/services');
    return response.data.data || [];
  } catch (error) {
    console.error('Ошибка получения списка сервисов:', error);
    // Fallback список сервисов на клиенте при любых ошибках
    return [
      {
        id: 'karbi-workplane',
        name: 'Задачи',
        description: 'Таск-менеджер команды. Управление задачами и уведомлениями.',
        icon: 'clipboard-list',
        url: '/workplane',
        permission: 'all',
        color: '#4F46E5',
        hasAccess: true,
      },
      {
        id: 'karbi-pay',
        name: 'KARBI PAY',
        description: 'Платежная система. Создание платежных ссылок, управление оплатами и возвратами.',
        icon: 'credit-card',
        url: '/pay',
        permission: 'pay:access',
        color: '#4F46E5',
        hasAccess: true,
      },
      {
        id: 'database',
        name: 'Базы данных',
        description: 'Управление базами данных PostgreSQL через Adminer.',
        icon: 'database',
        url: '/adminer',
        permission: 'all',
        color: '#0891b2',
        hasAccess: true,
      },
    ];
  }
};

// Profile
export const getProfile = async (): Promise<User> => {
  const response = await api.get<ProfileResponse>('/api/profile');
  return response.data.data!;
};

export const updateProfile = async (data: { name?: string; email?: string; phone?: string; avatar_url?: string; avatar_color?: string }): Promise<User> => {
  const response = await api.put<ProfileResponse>('/api/profile', data);
  return response.data.data!;
};

export const uploadAvatar = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('avatar', file);
  const response = await api.post<ProfileResponse>('/api/profile/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data!.avatar_url!;
};

export const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await api.post('/api/profile/change-password', { currentPassword, newPassword });
};

// Deployment History
export const getDeploymentHistory = async (page = 1) => {
  const response = await api.get('/api/deployment-history', { params: { page } });
  return response.data;
};

// Avito Bridge
export const getAvitoConversations = async () => {
  const response = await api.get('/api/avito-bridge/conversations');
  return response.data || [];
};

export const getAvitoMessages = async (conversationId: string, limit = 50) => {
  const response = await api.get(`/api/avito-bridge/conversations/${conversationId}/messages`, {
    params: { limit },
  });
  return response.data || [];
};

export const sendAvitoMessage = async (conversationId: string, text: string) => {
  const response = await api.post('/api/avito-bridge/send', { conversation_id: conversationId, text });
  return response.data;
};

export const getAvitoSettings = async () => {
  const response = await api.get('/api/avito-bridge/settings');
  return response.data;
};

export const saveAvitoSettings = async (data: {
  bitrix24OpenLineId?: string;
  bitrix24WebhookUrl?: string;
  bitrix24WebhookToken?: string;
  avitoUserId?: string;
  clientId?: string;
  clientSecret?: string;
}) => {
  const response = await api.post('/api/avito-bridge/settings/bitrix24', data);
  return response.data;
};

export const saveAvitoAvitoSettings = async (data: {
  avitoUserId?: string;
  clientId?: string;
  clientSecret?: string;
}) => {
  const response = await api.post('/api/avito-bridge/settings/avito', data);
  return response.data;
};

// Hub API (universal routing)
export const getHubSources = async () => {
  const response = await api.get('/api/avito-bridge/hub/sources');
  return response.data;
};

export const createHubSource = async (data: {
  source_type: string;
  account_id: string;
  display_name?: string;
  credentials?: any;
  config?: any;
}) => {
  const response = await api.post('/api/avito-bridge/hub/sources', data);
  return response.data;
};

export const getHubLines = async () => {
  const response = await api.get('/api/avito-bridge/hub/lines');
  return response.data;
};

export const createHubLine = async (data: {
  display_name: string;
  webhook_url: string;
  webhook_token?: string;
}) => {
  const response = await api.post('/api/avito-bridge/hub/lines', data);
  return response.data;
};

export const getHubQueues = async () => {
  const response = await api.get('/api/avito-bridge/hub/queues');
  return response.data;
};

export const createHubQueue = async (data: {
  name: string;
  bitrix24_line_id?: string;
}) => {
  const response = await api.post('/api/avito-bridge/hub/queues', data);
  return response.data;
};

export const getHubRules = async () => {
  const response = await api.get('/api/avito-bridge/hub/rules');
  return response.data;
};

export const createHubRule = async (data: {
  source_id: string;
  bitrix24_line_id: string;
  queue_id?: string;
  name?: string;
  priority?: number;
  conditions?: any;
}) => {
  const response = await api.post('/api/avito-bridge/hub/rules', data);
  return response.data;
};

// Workplace Settings
export const getWorkplaceSetting = async (key: string): Promise<any | null> => {
  const response = await api.get(`/api/workplace/settings/${key}`);
  return response.data.data || null;
};

export const saveWorkplaceSetting = async (key: string, value: any): Promise<void> => {
  await api.put(`/api/workplace/settings/${key}`, { value });
};

export default api;
